import { useState, useEffect, useCallback } from "react";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import {
  Send, Users, MessageCircle, Radio, BarChart3,
  RefreshCw, Clock, CheckCircle2, XCircle, Bot, Zap, Globe,
  Plus, Trash2, Edit2, ToggleLeft, ToggleRight,
  Mail, Phone, Workflow, ChevronDown, ChevronUp, X,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TRIGGER_LABELS = {
  on_start: "Nuovo iscritto",
  on_course: "Iscrizione corso",
  on_booking: "Prenotazione",
  on_wellness: "Pillola benessere",
  manual: "Manuale",
};

const CHANNEL_ICONS = { telegram: Bot, email: Mail, whatsapp: Phone };
const CHANNEL_COLORS = { telegram: "blue", email: "emerald", whatsapp: "green" };

export default function AdminTelegramPage() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [broadcastText, setBroadcastText] = useState("");
  const [sending, setSending] = useState(false);
  const [broadcastResult, setBroadcastResult] = useState(null);
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [selectedUser, setSelectedUser] = useState(null);
  const [directMsg, setDirectMsg] = useState("");
  const [sendingDirect, setSendingDirect] = useState(false);

  // Automations state
  const [automations, setAutomations] = useState([]);
  const [autoStats, setAutoStats] = useState(null);
  const [autoLogs, setAutoLogs] = useState([]);
  const [showAutoForm, setShowAutoForm] = useState(false);
  const [editingAuto, setEditingAuto] = useState(null);
  const [autoForm, setAutoForm] = useState({
    name: "", trigger: "on_start", channels: ["telegram"],
    telegram_message: "", email_subject: "", email_body: "", whatsapp_message: "", is_active: true,
  });

  // Quick send state
  const [quickSend, setQuickSend] = useState({
    channels: ["telegram"], message: "", email_subject: "ODR Academy",
    telegram_broadcast: true, emails: "", phones: "",
  });
  const [quickResult, setQuickResult] = useState(null);
  const [quickSending, setQuickSending] = useState(false);

  const token = localStorage.getItem("token");
  const headers = { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" };

  const fetchAll = useCallback(async () => {
    const fetches = [
      fetch(`${API}/api/telegram/stats`, { headers }).then(r => r.ok ? r.json() : null),
      fetch(`${API}/api/telegram/users`, { headers }).then(r => r.ok ? r.json() : null),
      fetch(`${API}/api/telegram/messages?limit=50`, { headers }).then(r => r.ok ? r.json() : null),
      fetch(`${API}/api/telegram/webhook-info`, { headers }).then(r => r.ok ? r.json() : null),
      fetch(`${API}/api/automations/rules`, { headers }).then(r => r.ok ? r.json() : null),
      fetch(`${API}/api/automations/stats`, { headers }).then(r => r.ok ? r.json() : null),
      fetch(`${API}/api/automations/logs?limit=30`, { headers }).then(r => r.ok ? r.json() : null),
    ];
    const [s, u, m, w, ar, as2, al] = await Promise.all(fetches);
    if (s) setStats(s);
    if (u) setUsers(u.users || []);
    if (m) setMessages(m.messages || []);
    if (w) setWebhookInfo(w);
    if (ar) setAutomations(ar.rules || []);
    if (as2) setAutoStats(as2);
    if (al) setAutoLogs(al.logs || []);
  }, []);

  useEffect(() => { fetchAll(); }, []);

  const setupWebhook = async () => {
    const res = await fetch(`${API}/api/telegram/setup-webhook`, { method: "POST", headers });
    if (res.ok) { alert("Webhook configurato!"); fetchAll(); }
  };

  const sendBroadcast = async () => {
    if (!broadcastText.trim() || sending) return;
    setSending(true); setBroadcastResult(null);
    const res = await fetch(`${API}/api/telegram/broadcast`, { method: "POST", headers, body: JSON.stringify({ message: broadcastText }) });
    if (res.ok) { setBroadcastResult(await res.json()); setBroadcastText(""); fetchAll(); }
    setSending(false);
  };

  const sendDirectMessage = async () => {
    if (!directMsg.trim() || !selectedUser || sendingDirect) return;
    setSendingDirect(true);
    const res = await fetch(`${API}/api/telegram/send`, { method: "POST", headers, body: JSON.stringify({ chat_id: selectedUser.chat_id, message: directMsg }) });
    if (res.ok) { setDirectMsg(""); fetchAll(); }
    setSendingDirect(false);
  };

  // Automation CRUD
  const saveAutomation = async () => {
    const url = editingAuto
      ? `${API}/api/automations/rules/${editingAuto}`
      : `${API}/api/automations/rules`;
    const method = editingAuto ? "PUT" : "POST";
    const res = await fetch(url, { method, headers, body: JSON.stringify(autoForm) });
    if (res.ok) { setShowAutoForm(false); setEditingAuto(null); resetAutoForm(); fetchAll(); }
  };

  const deleteAutomation = async (id) => {
    if (!window.confirm("Eliminare questa automazione?")) return;
    await fetch(`${API}/api/automations/rules/${id}`, { method: "DELETE", headers });
    fetchAll();
  };

  const toggleAutomation = async (id) => {
    await fetch(`${API}/api/automations/rules/${id}/toggle`, { method: "POST", headers });
    fetchAll();
  };

  const editAutomation = (auto) => {
    setAutoForm({
      name: auto.name, trigger: auto.trigger, channels: auto.channels,
      telegram_message: auto.telegram_message || "", email_subject: auto.email_subject || "",
      email_body: auto.email_body || "", whatsapp_message: auto.whatsapp_message || "",
      is_active: auto.is_active,
    });
    setEditingAuto(auto.automation_id);
    setShowAutoForm(true);
  };

  const resetAutoForm = () => {
    setAutoForm({ name: "", trigger: "on_start", channels: ["telegram"], telegram_message: "", email_subject: "", email_body: "", whatsapp_message: "", is_active: true });
  };

  // Quick send
  const handleQuickSend = async () => {
    if (!quickSend.message.trim() || quickSending) return;
    setQuickSending(true); setQuickResult(null);
    const body = {
      channels: quickSend.channels,
      message: quickSend.message,
      email_subject: quickSend.email_subject,
      telegram_broadcast: quickSend.telegram_broadcast,
      telegram_chat_ids: [],
      emails: quickSend.emails ? quickSend.emails.split(",").map(e => e.trim()).filter(Boolean) : [],
      phones: quickSend.phones ? quickSend.phones.split(",").map(p => p.trim()).filter(Boolean) : [],
    };
    const res = await fetch(`${API}/api/automations/quick-send`, { method: "POST", headers, body: JSON.stringify(body) });
    if (res.ok) { setQuickResult(await res.json()); fetchAll(); }
    setQuickSending(false);
  };

  const toggleChannel = (ch) => {
    setAutoForm(f => ({
      ...f, channels: f.channels.includes(ch) ? f.channels.filter(c => c !== ch) : [...f.channels, ch],
    }));
  };

  const toggleQuickChannel = (ch) => {
    setQuickSend(f => ({
      ...f, channels: f.channels.includes(ch) ? f.channels.filter(c => c !== ch) : [...f.channels, ch],
    }));
  };

  const formatDate = (d) => {
    if (!d) return "-";
    try { return new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch { return d; }
  };

  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: BarChart3 },
    { id: "automations", label: "Automazioni", icon: Workflow },
    { id: "quicksend", label: "Invio Rapido", icon: Send },
    { id: "broadcast", label: "Broadcast TG", icon: Radio },
    { id: "users", label: "Utenti", icon: Users },
    { id: "messages", label: "Messaggi", icon: MessageCircle },
    { id: "logs", label: "Log Attivita", icon: Clock },
    { id: "settings", label: "Webhook", icon: Globe },
  ];

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Navbar />
      <main className="flex-1 p-6 md:p-8 ml-0 md:ml-64" data-testid="admin-telegram-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900" data-testid="page-title">Telegram & Automazioni</h1>
              <p className="text-sm text-gray-500">Bot, automazioni multi-canale e invio rapido</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchAll} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Aggiorna
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-gray-200 overflow-x-auto" data-testid="tabs">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-3 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.id ? "border-blue-500 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700"}`} data-testid={`tab-${tab.id}`}>
                <Icon className="w-4 h-4" /> {tab.label}
              </button>
            );
          })}
        </div>

        {/* ====== DASHBOARD ====== */}
        {activeTab === "dashboard" && (
          <div className="space-y-6" data-testid="dashboard-tab">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: "Utenti Telegram", value: stats?.total_users || 0, icon: Users, bg: "bg-blue-50", tc: "text-blue-500" },
                { label: "Automazioni attive", value: autoStats?.active_rules || 0, icon: Workflow, bg: "bg-purple-50", tc: "text-purple-500" },
                { label: "Messaggi totali", value: stats?.total_messages || 0, icon: MessageCircle, bg: "bg-indigo-50", tc: "text-indigo-500" },
                { label: "Email inviate", value: autoStats?.email_sent || 0, icon: Mail, bg: "bg-emerald-50", tc: "text-emerald-500" },
              ].map((s, i) => {
                const Icon = s.icon;
                return (
                  <div key={i} className="bg-white rounded-xl p-6 border border-gray-100" data-testid={`stat-card-${i}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${s.bg}`}><Icon className={`w-4 h-4 ${s.tc}`} /></div>
                      <span className="text-xs text-gray-500">{s.label}</span>
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{s.value}</p>
                  </div>
                );
              })}
            </div>

            {/* Multi-channel stats */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Canali di comunicazione</h3>
              <div className="grid grid-cols-3 gap-6">
                {[
                  { label: "Telegram", value: autoStats?.telegram_sent || 0, icon: Bot, color: "blue" },
                  { label: "Email", value: autoStats?.email_sent || 0, icon: Mail, color: "emerald" },
                  { label: "WhatsApp", value: autoStats?.whatsapp_generated || 0, icon: Phone, color: "green" },
                ].map((ch, i) => {
                  const Icon = ch.icon;
                  return (
                    <div key={i} className="text-center p-4 rounded-xl bg-gray-50">
                      <Icon className={`w-6 h-6 mx-auto mb-2 text-${ch.color}-500`} />
                      <p className="text-2xl font-bold text-gray-900">{ch.value}</p>
                      <p className="text-xs text-gray-500">{ch.label} inviati</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Recent activity */}
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-gray-100">
                <div className="p-4 border-b border-gray-100"><h3 className="font-semibold text-gray-900">Ultimi messaggi</h3></div>
                <div className="p-4 space-y-3 max-h-72 overflow-y-auto">
                  {messages.slice(0, 6).map((msg, i) => (
                    <div key={i} className="flex items-start gap-3 text-sm">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${msg.direction === "incoming" ? "bg-blue-50" : "bg-green-50"}`}>
                        {msg.direction === "incoming" ? <MessageCircle className="w-3 h-3 text-blue-500" /> : <Send className="w-3 h-3 text-green-500" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-900 truncate">{msg.text?.substring(0, 60)}</p>
                        <p className="text-xs text-gray-400">{msg.first_name || msg.username || ""} — {formatDate(msg.timestamp)}</p>
                      </div>
                    </div>
                  ))}
                  {messages.length === 0 && <p className="text-sm text-gray-400 text-center py-6">Nessun messaggio</p>}
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-100">
                <div className="p-4 border-b border-gray-100"><h3 className="font-semibold text-gray-900">Ultimi iscritti</h3></div>
                <div className="p-4 space-y-3 max-h-72 overflow-y-auto">
                  {users.slice(0, 6).map((user, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-medium text-xs">{(user.first_name || "U")[0]}</div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{user.first_name} {user.last_name || ""}</p>
                        <p className="text-xs text-gray-400">@{user.username || "—"}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${user.is_active ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-500"}`}>{user.is_active ? "Attivo" : "Inattivo"}</span>
                    </div>
                  ))}
                  {users.length === 0 && <p className="text-sm text-gray-400 text-center py-6">Nessun utente</p>}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ====== AUTOMATIONS ====== */}
        {activeTab === "automations" && (
          <div className="space-y-6" data-testid="automations-tab">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Regole di automazione</h3>
              <Button onClick={() => { resetAutoForm(); setEditingAuto(null); setShowAutoForm(true); }} className="bg-blue-500 hover:bg-blue-600" data-testid="add-automation-btn">
                <Plus className="w-4 h-4 mr-2" /> Nuova automazione
              </Button>
            </div>

            {/* Automation Form Modal */}
            {showAutoForm && (
              <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowAutoForm(false)}>
                <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="automation-form">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold">{editingAuto ? "Modifica" : "Nuova"} Automazione</h3>
                    <button onClick={() => setShowAutoForm(false)}><X className="w-5 h-5 text-gray-400" /></button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-1">Nome</label>
                      <input value={autoForm.name} onChange={e => setAutoForm(f => ({...f, name: e.target.value}))} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Es: Benvenuto Nuovo Iscritto" data-testid="auto-name" />
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-1">Trigger</label>
                      <select value={autoForm.trigger} onChange={e => setAutoForm(f => ({...f, trigger: e.target.value}))} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="auto-trigger">
                        {Object.entries(TRIGGER_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-2">Canali</label>
                      <div className="flex gap-3">
                        {["telegram", "email", "whatsapp"].map(ch => (
                          <button key={ch} onClick={() => toggleChannel(ch)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${autoForm.channels.includes(ch) ? "bg-blue-500 text-white" : "bg-gray-100 text-gray-600"}`} data-testid={`auto-ch-${ch}`}>
                            {ch === "telegram" ? <Bot className="w-4 h-4" /> : ch === "email" ? <Mail className="w-4 h-4" /> : <Phone className="w-4 h-4" />}
                            {ch.charAt(0).toUpperCase() + ch.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>

                    {autoForm.channels.includes("telegram") && (
                      <div>
                        <label className="text-sm font-medium text-gray-700 block mb-1">Messaggio Telegram (HTML)</label>
                        <textarea value={autoForm.telegram_message} onChange={e => setAutoForm(f => ({...f, telegram_message: e.target.value}))} rows={3} className="w-full border rounded-lg px-3 py-2 text-sm resize-none" placeholder="Usa {nome} per il nome utente" data-testid="auto-tg-msg" />
                      </div>
                    )}

                    {autoForm.channels.includes("email") && (
                      <>
                        <div>
                          <label className="text-sm font-medium text-gray-700 block mb-1">Oggetto Email</label>
                          <input value={autoForm.email_subject} onChange={e => setAutoForm(f => ({...f, email_subject: e.target.value}))} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Es: Benvenuto in ODR Academy" data-testid="auto-email-subj" />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-700 block mb-1">Corpo Email (HTML)</label>
                          <textarea value={autoForm.email_body} onChange={e => setAutoForm(f => ({...f, email_body: e.target.value}))} rows={3} className="w-full border rounded-lg px-3 py-2 text-sm resize-none" placeholder="<h2>Benvenuto {nome}!</h2>" data-testid="auto-email-body" />
                        </div>
                      </>
                    )}

                    {autoForm.channels.includes("whatsapp") && (
                      <div>
                        <label className="text-sm font-medium text-gray-700 block mb-1">Messaggio WhatsApp</label>
                        <textarea value={autoForm.whatsapp_message} onChange={e => setAutoForm(f => ({...f, whatsapp_message: e.target.value}))} rows={2} className="w-full border rounded-lg px-3 py-2 text-sm resize-none" placeholder="Usa {nome} per il nome" data-testid="auto-wa-msg" />
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3 mt-6 pt-4 border-t">
                    <Button onClick={saveAutomation} className="bg-blue-500 hover:bg-blue-600 flex-1" data-testid="save-automation-btn">
                      {editingAuto ? "Aggiorna" : "Crea"} Automazione
                    </Button>
                    <Button variant="outline" onClick={() => setShowAutoForm(false)}>Annulla</Button>
                  </div>
                </div>
              </div>
            )}

            {/* Automation List */}
            <div className="space-y-3">
              {automations.map((auto, i) => (
                <div key={auto.automation_id} className="bg-white rounded-xl border border-gray-100 p-5 flex items-center gap-4" data-testid={`automation-card-${i}`}>
                  <button onClick={() => toggleAutomation(auto.automation_id)} className="flex-shrink-0" data-testid={`toggle-auto-${i}`}>
                    {auto.is_active ? <ToggleRight className="w-7 h-7 text-blue-500" /> : <ToggleLeft className="w-7 h-7 text-gray-300" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold text-gray-900">{auto.name}</p>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">{TRIGGER_LABELS[auto.trigger] || auto.trigger}</span>
                    </div>
                    <div className="flex gap-2">
                      {(auto.channels || []).map(ch => {
                        const Icon = CHANNEL_ICONS[ch] || Bot;
                        return <span key={ch} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-50 text-gray-600"><Icon className="w-3 h-3" />{ch}</span>;
                      })}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => editAutomation(auto)} data-testid={`edit-auto-${i}`}><Edit2 className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteAutomation(auto.automation_id)} data-testid={`delete-auto-${i}`}><Trash2 className="w-4 h-4 text-red-400" /></Button>
                  </div>
                </div>
              ))}
              {automations.length === 0 && <p className="text-center text-gray-400 py-12">Nessuna automazione. Creane una!</p>}
            </div>
          </div>
        )}

        {/* ====== QUICK SEND ====== */}
        {activeTab === "quicksend" && (
          <div className="max-w-2xl space-y-6" data-testid="quicksend-tab">
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="font-semibold text-gray-900 mb-1">Invio rapido multi-canale</h3>
              <p className="text-sm text-gray-500 mb-6">Invia un messaggio simultaneo su Telegram, Email e WhatsApp.</p>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">Canali</label>
                  <div className="flex gap-3">
                    {["telegram", "email", "whatsapp"].map(ch => (
                      <button key={ch} onClick={() => toggleQuickChannel(ch)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${quickSend.channels.includes(ch) ? "bg-blue-500 text-white" : "bg-gray-100 text-gray-600"}`} data-testid={`quick-ch-${ch}`}>
                        {ch === "telegram" ? <Bot className="w-4 h-4" /> : ch === "email" ? <Mail className="w-4 h-4" /> : <Phone className="w-4 h-4" />}
                        {ch.charAt(0).toUpperCase() + ch.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Messaggio</label>
                  <textarea value={quickSend.message} onChange={e => setQuickSend(f => ({...f, message: e.target.value}))} rows={4} className="w-full border rounded-lg px-3 py-2 text-sm resize-none" placeholder="Scrivi il messaggio..." data-testid="quick-message" />
                </div>

                {quickSend.channels.includes("telegram") && (
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={quickSend.telegram_broadcast} onChange={e => setQuickSend(f => ({...f, telegram_broadcast: e.target.checked}))} className="rounded" data-testid="quick-tg-broadcast" />
                    Broadcast a tutti gli utenti Telegram attivi ({stats?.active_users || 0})
                  </label>
                )}

                {quickSend.channels.includes("email") && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-1">Oggetto email</label>
                    <input value={quickSend.email_subject} onChange={e => setQuickSend(f => ({...f, email_subject: e.target.value}))} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="quick-email-subj" />
                    <label className="text-sm font-medium text-gray-700 block mb-1 mt-3">Destinatari email (separati da virgola)</label>
                    <input value={quickSend.emails} onChange={e => setQuickSend(f => ({...f, emails: e.target.value}))} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="email1@example.com, email2@example.com" data-testid="quick-emails" />
                  </div>
                )}

                {quickSend.channels.includes("whatsapp") && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-1">Numeri WhatsApp (separati da virgola, con prefisso)</label>
                    <input value={quickSend.phones} onChange={e => setQuickSend(f => ({...f, phones: e.target.value}))} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="+393331234567, +393339876543" data-testid="quick-phones" />
                  </div>
                )}

                <Button onClick={handleQuickSend} disabled={!quickSend.message.trim() || quickSending} className="bg-blue-500 hover:bg-blue-600 w-full" data-testid="quick-send-btn">
                  <Send className="w-4 h-4 mr-2" /> {quickSending ? "Invio in corso..." : "Invia su tutti i canali"}
                </Button>
              </div>

              {quickResult && (
                <div className="mt-6 p-4 rounded-xl bg-green-50 border border-green-100" data-testid="quick-result">
                  <div className="flex items-center gap-2 mb-2"><CheckCircle2 className="w-4 h-4 text-green-500" /><span className="font-medium text-green-800">Invio completato</span></div>
                  <div className="text-sm text-green-700 space-y-1">
                    {quickResult.telegram_sent > 0 && <p>Telegram: <strong>{quickResult.telegram_sent}</strong> inviati</p>}
                    {quickResult.email_sent > 0 && <p>Email: <strong>{quickResult.email_sent}</strong> inviate</p>}
                    {quickResult.whatsapp_links?.length > 0 && (
                      <div>
                        <p>WhatsApp: <strong>{quickResult.whatsapp_links.length}</strong> link generati</p>
                        {quickResult.whatsapp_links.map((l, i) => (
                          <a key={i} href={l.url} target="_blank" rel="noopener noreferrer" className="block text-xs text-blue-600 underline mt-1">Apri chat con {l.phone}</a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ====== BROADCAST ====== */}
        {activeTab === "broadcast" && (
          <div className="max-w-2xl space-y-6" data-testid="broadcast-tab">
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="font-semibold text-gray-900 mb-1">Broadcast Telegram</h3>
              <p className="text-sm text-gray-500 mb-6">Invia un messaggio a tutti gli utenti attivi del bot.</p>
              <textarea value={broadcastText} onChange={e => setBroadcastText(e.target.value)} rows={6} className="w-full border rounded-lg px-3 py-2 text-sm resize-none mb-4" placeholder="<b>Titolo</b>&#10;Testo..." data-testid="broadcast-textarea" />
              <div className="flex items-center gap-4">
                <Button onClick={sendBroadcast} disabled={!broadcastText.trim() || sending} className="bg-blue-500 hover:bg-blue-600" data-testid="send-broadcast-btn">
                  <Radio className="w-4 h-4 mr-2" /> {sending ? "Invio..." : "Invia a tutti"}
                </Button>
                <span className="text-xs text-gray-400">{stats?.active_users || 0} utenti attivi</span>
              </div>
              {broadcastResult && (
                <div className="mt-4 p-4 rounded-xl bg-green-50 border border-green-100" data-testid="broadcast-result">
                  <p className="text-sm text-green-700">Inviati: <strong>{broadcastResult.sent}</strong> — Falliti: <strong>{broadcastResult.failed}</strong></p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ====== USERS ====== */}
        {activeTab === "users" && (
          <div className="space-y-4" data-testid="users-tab">
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="p-4 border-b border-gray-100"><h3 className="font-semibold text-gray-900">Utenti Telegram ({users.length})</h3></div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-gray-500">Utente</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500">Username</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500">Chat ID</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500">Registrato</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500">Stato</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500">Azioni</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {users.map((user, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium">{user.first_name} {user.last_name || ""}</td>
                        <td className="px-4 py-3 text-gray-500">@{user.username || "—"}</td>
                        <td className="px-4 py-3 font-mono text-xs text-gray-500">{user.chat_id}</td>
                        <td className="px-4 py-3 text-gray-500">{formatDate(user.registered_at)}</td>
                        <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full ${user.is_active ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-500"}`}>{user.is_active ? "Attivo" : "Bloccato"}</span></td>
                        <td className="px-4 py-3"><Button variant="ghost" size="sm" onClick={() => { setSelectedUser(user); setActiveTab("messages"); }} data-testid={`msg-user-${i}`}><MessageCircle className="w-4 h-4" /></Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {users.length === 0 && <p className="text-center text-gray-400 py-12">Nessun utente registrato</p>}
              </div>
            </div>
          </div>
        )}

        {/* ====== MESSAGES ====== */}
        {activeTab === "messages" && (
          <div className="space-y-4" data-testid="messages-tab">
            {selectedUser && (
              <div className="bg-white rounded-xl border border-gray-100 p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-medium text-sm">{(selectedUser.first_name || "U")[0]}</div>
                    <div>
                      <p className="font-medium text-gray-900">{selectedUser.first_name} {selectedUser.last_name || ""}</p>
                      <p className="text-xs text-gray-400">Chat ID: {selectedUser.chat_id}</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedUser(null)}>Tutti</Button>
                </div>
                <div className="flex gap-2">
                  <input value={directMsg} onChange={e => setDirectMsg(e.target.value)} onKeyDown={e => e.key === "Enter" && sendDirectMessage()} placeholder="Messaggio diretto..." className="flex-1 border rounded-lg px-3 py-2 text-sm" data-testid="direct-msg-input" />
                  <Button onClick={sendDirectMessage} disabled={!directMsg.trim() || sendingDirect} className="bg-blue-500 hover:bg-blue-600" data-testid="send-direct-btn"><Send className="w-4 h-4" /></Button>
                </div>
              </div>
            )}
            <div className="bg-white rounded-xl border border-gray-100">
              <div className="p-4 border-b border-gray-100"><h3 className="font-semibold text-gray-900">{selectedUser ? `Chat con ${selectedUser.first_name}` : "Tutti i messaggi"}</h3></div>
              <div className="p-4 space-y-3 max-h-[500px] overflow-y-auto">
                {messages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.direction === "outgoing" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 text-sm ${msg.direction === "outgoing" ? "bg-blue-500 text-white" : "bg-gray-100 text-gray-900"}`}>
                      <p>{msg.text?.substring(0, 300)}</p>
                      <p className={`text-xs mt-1 ${msg.direction === "outgoing" ? "text-blue-200" : "text-gray-400"}`}>{formatDate(msg.timestamp)}</p>
                    </div>
                  </div>
                ))}
                {messages.length === 0 && <p className="text-center text-gray-400 py-12">Nessun messaggio</p>}
              </div>
            </div>
          </div>
        )}

        {/* ====== LOGS ====== */}
        {activeTab === "logs" && (
          <div className="space-y-4" data-testid="logs-tab">
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="p-4 border-b border-gray-100"><h3 className="font-semibold text-gray-900">Log attivita automazioni</h3></div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-gray-500">Data</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500">Canale</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500">Destinatario</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500">Stato</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-500">Dettagli</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {autoLogs.map((log, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-500">{formatDate(log.timestamp)}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-50 text-gray-600">
                            {log.channel === "telegram" ? <Bot className="w-3 h-3" /> : log.channel === "email" ? <Mail className="w-3 h-3" /> : <Phone className="w-3 h-3" />}
                            {log.channel}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-gray-500">{log.recipient?.substring(0, 30)}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-1 rounded-full ${log.status === "sent" || log.status === "link_generated" ? "bg-green-50 text-green-600" : "bg-red-50 text-red-600"}`}>{log.status}</span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-400 max-w-[200px] truncate">{log.details}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {autoLogs.length === 0 && <p className="text-center text-gray-400 py-12">Nessuna attivita registrata</p>}
              </div>
            </div>
          </div>
        )}

        {/* ====== WEBHOOK SETTINGS ====== */}
        {activeTab === "settings" && (
          <div className="max-w-2xl" data-testid="settings-tab">
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Webhook Telegram</h3>
              {webhookInfo?.result ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">URL</p>
                      <p className="text-sm font-mono text-gray-900 break-all">{webhookInfo.result.url || "Non configurato"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Stato</p>
                      {webhookInfo.result.url ? (
                        <span className="inline-flex items-center gap-1 text-sm text-green-600"><CheckCircle2 className="w-4 h-4" /> Attivo</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-sm text-red-600"><XCircle className="w-4 h-4" /> Non attivo</span>
                      )}
                    </div>
                  </div>
                  {webhookInfo.result.last_error_message && (
                    <div className="p-3 rounded-lg bg-red-50"><p className="text-sm text-red-600">{webhookInfo.result.last_error_message}</p></div>
                  )}
                </div>
              ) : <p className="text-sm text-gray-500">Caricamento...</p>}
              <div className="mt-6 pt-4 border-t">
                <Button onClick={setupWebhook} className="bg-blue-500 hover:bg-blue-600" data-testid="setup-webhook-btn">
                  <Globe className="w-4 h-4 mr-2" /> {webhookInfo?.result?.url ? "Riconfigura" : "Configura"} Webhook
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
